/**
 * Locked automated surveys that were not locked yet when this page loaded.
 *
 * The bootstrap state only auto-starts assignments whose `locked_at` has already
 * passed, and `is_locked_now` is a server snapshot that never flips client-side.
 * This module remembers the upcoming lock times (LoadingState
 * `UpcomingAutomatedSurveyLocks`, plus live notifications) so the router guard can
 * move the learner into the survey on their next navigation once a lock comes due
 * mid-session (ClickUp 868k2749y). The current page is never interrupted.
 *
 * The list is only a hint for *when* to ask; the server's next-in-queue endpoint
 * decides *what* to start, so completed or released assignments never start.
 */
import { initialState } from '@/Helpers.js';
import axios from '@/plugins/axios.js';

export { DUE_LOCKED_SURVEY_CODE } from '@/utilities/dueLockRejection.js';

/** Minimum time between server checks after one found nothing to start. */
export const LOCK_CHECK_THROTTLE_MS = 10 * 1000;

/**
 * A due lock the server did not confirm is kept this long (to absorb clock and
 * render-time drift) before it is treated as completed or released and dropped.
 */
export const LOCK_CONFIRMATION_GRACE_MS = 30 * 1000;

/** Failed starts back off exponentially from the throttle up to this cap. */
export const LOCK_FAILURE_BACKOFF_MAX_MS = 5 * 60 * 1000;

/** At most one lock-related toast is shown per window. */
export const LOCK_NOTICE_INTERVAL_MS = 60 * 1000;

let upcomingLocks = null;
let lastEmptyCheckAt = null;
let lastFailureAt = null;
let consecutiveFailures = 0;
let lastNoticeAt = null;
let redirecting = false;
let serverReportedDueLock = false;

/**
 * The server says whether this user may start automated surveys at all
 * (AutomatedSurveyLockGate::canStart). Deployment targets can be any user; never
 * enforce a lock the start endpoint would reject.
 *
 * @returns {boolean}
 */
export function canEnforceLocks() {
    return initialState('CanStartAutomatedSurveys', true) !== false;
}

/**
 * Current wait after a failed start: throttle, doubling per consecutive failure, capped.
 *
 * @returns {number}
 */
export function failureBackoffMs() {
    if (consecutiveFailures === 0) {
        return 0;
    }

    return Math.min(LOCK_CHECK_THROTTLE_MS * 2 ** (consecutiveFailures - 1), LOCK_FAILURE_BACKOFF_MAX_MS);
}

function pageTimeOrigin() {
    const origin = globalThis.performance?.timeOrigin;

    return Number.isFinite(origin) && origin > 0 ? origin : Date.now();
}

/**
 * Laravel serializes model dates as UTC `Y-m-d H:i:s`; the bootstrap payload uses ISO 8601.
 *
 * @param {string|null|undefined} value
 * @returns {number} Epoch milliseconds, or NaN.
 */
export function parseServerDate(value) {
    if (!value) {
        return Number.NaN;
    }

    const text = String(value);
    const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(text);

    return Date.parse(hasZone ? text : `${text.replace(' ', 'T')}Z`);
}

function toLock(assignment, lockAt) {
    return {
        id: assignment.id,
        survey_id: assignment.survey_id,
        course_id: assignment.course_id ?? null,
        lockAt,
    };
}

function locks() {
    if (!canEnforceLocks()) {
        return [];
    }

    if (upcomingLocks === null) {
        // `seconds_until_lock` is relative to server render time, which is just after
        // navigation start, so it does not depend on the learner's clock.
        const origin = pageTimeOrigin();

        upcomingLocks = initialState('UpcomingAutomatedSurveyLocks', [])
            .map(lock =>
                toLock(
                    lock,
                    Number.isFinite(lock.seconds_until_lock)
                        ? origin + lock.seconds_until_lock * 1000
                        : parseServerDate(lock.locked_at)
                )
            )
            .filter(lock => Number.isFinite(lock.lockAt));
    }

    return upcomingLocks;
}

/**
 * Remember an assignment delivered after page load (live notification).
 *
 * @param {Object} assignment
 * @returns {boolean} Whether a future lock was recorded.
 */
export function trackUpcomingLock(assignment) {
    const lockAt = parseServerDate(assignment?.locked_at);

    if (
        !canEnforceLocks() ||
        !assignment?.id ||
        assignment.completed_at ||
        assignment.is_lock_enforced === false ||
        !Number.isFinite(lockAt)
    ) {
        return false;
    }

    upcomingLocks = [...locks().filter(lock => lock.id !== assignment.id), toLock(assignment, lockAt)];

    return true;
}

/**
 * @param {number} [now]
 * @returns {boolean}
 */
export function hasDueLock(now = Date.now()) {
    return (canEnforceLocks() && serverReportedDueLock) || locks().some(lock => lock.lockAt <= now);
}

/**
 * The server refused a request because a locked survey is due but unstarted
 * (`automated_survey_locked`). The page may not have known about the lock; make
 * the next navigation enforce it.
 */
export function noteServerReportedDueLock() {
    serverReportedDueLock = true;
}

/** @returns {boolean} Whether the next check should bypass the empty-answer throttle. */
export function isServerReportedDueLock() {
    return serverReportedDueLock;
}

/**
 * Ask the server for the locked assignment the learner must start now.
 * Callers are expected to call this only when `hasDueLock()`; repeated empty
 * answers are throttled so navigation cannot cause a request storm.
 *
 * @param {Object} [options]
 * @param {boolean} [options.force] Skip the throttle (server already reported a due lock).
 * @returns {Promise<Object|null>}
 */
export async function fetchDueLockedAssignment({ force = false } = {}) {
    const now = Date.now();
    const backingOff = lastFailureAt !== null && now - lastFailureAt < failureBackoffMs();
    const throttled = lastEmptyCheckAt !== null && now - lastEmptyCheckAt < LOCK_CHECK_THROTTLE_MS;

    // A failed start is never retried inside its backoff, even when forced.
    if (backingOff || (!force && throttled)) {
        return null;
    }

    const { data } = await axios.get('/api/automated-survey-attempts/next-in-queue', { errorHandle: false });
    const assignment = data?.data ?? null;

    if (assignment) {
        lastEmptyCheckAt = null;

        return assignment;
    }

    lastEmptyCheckAt = now;
    serverReportedDueLock = false;
    consecutiveFailures = 0;
    lastFailureAt = null;
    // Nothing is due on the server: drop due locks once they are past the grace window.
    upcomingLocks = locks().filter(lock => lock.lockAt > now - LOCK_CONFIRMATION_GRACE_MS);

    return null;
}

/**
 * Record a failed start. Retries back off exponentially (capped). When the server
 * rejected the start itself (4xx: already started, expired, not startable, not
 * allowed), the due locks are dropped so neither the timer nor navigation keeps
 * retrying something that cannot succeed; later locks still apply.
 *
 * @param {Object} [options]
 * @param {boolean} [options.notStartable]
 * @param {number} [options.now]
 * @returns {number} Consecutive failures, including this one.
 */
export function recordLockStartFailure({ notStartable = false, now = Date.now() } = {}) {
    consecutiveFailures += 1;
    lastFailureAt = now;

    if (notStartable) {
        upcomingLocks = locks().filter(lock => lock.lockAt > now);
        serverReportedDueLock = false;
    }

    return consecutiveFailures;
}

/**
 * Claim the single lock-notice slot for this window so blocked requests and
 * failed starts never stack toasts.
 *
 * @param {number} [now]
 * @returns {boolean} Whether the caller may show a notice.
 */
export function claimLockNotice(now = Date.now()) {
    if (lastNoticeAt !== null && now - lastNoticeAt < LOCK_NOTICE_INTERVAL_MS) {
        return false;
    }

    lastNoticeAt = now;

    return true;
}

/** The page is already leaving for a survey; later triggers must not start another. */
export function markRedirectingToLockedSurvey() {
    redirecting = true;
}

export function isRedirectingToLockedSurvey() {
    return redirecting;
}

export function resetAutomatedSurveyLockSchedule() {
    upcomingLocks = null;
    lastEmptyCheckAt = null;
    lastFailureAt = null;
    consecutiveFailures = 0;
    lastNoticeAt = null;
    redirecting = false;
    serverReportedDueLock = false;
}
